import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Shield, ShieldCheck, ShieldOff, ShieldAlert, ArrowLeft, Search, Filter,
  Printer, Ban, Trash2, Edit2, CheckCircle, AlertTriangle, RefreshCcw,
  ArrowDownCircle, ArrowUpCircle, FileSignature, Plus, X, ClipboardCheck,
} from "lucide-react";
import { AuthContext } from "../App";
import { toast } from "sonner";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const hoyStr = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const fmtMoneda = (n, moneda) => {
  const val = Number(n || 0);
  if (moneda === "USD") return `US$ ${val.toLocaleString("es-PY", { minimumFractionDigits: 2 })}`;
  return `₲ ${val.toLocaleString("es-PY")}`;
};

const ESTADO_PAGO_BADGE = {
  pendiente: "bg-slate-700 text-slate-300",
  pagado:    "bg-emerald-900/60 text-emerald-300",
  vencido:   "bg-red-900/60 text-red-300",
  cancelado: "bg-slate-800 text-slate-500",
};
const ESTADO_PAGO_LABEL = { pendiente: "Pendiente", pagado: "Pagado", vencido: "Vencido", cancelado: "Cancelado" };

const ESTADO_DOC_BADGE = {
  vigente:   "bg-cyan-900/60 text-cyan-300",
  cobrado:   "bg-emerald-900/60 text-emerald-300",
  cancelado: "bg-slate-800 text-slate-500",
  anulado:   "bg-red-900/60 text-red-300",
  destruido: "bg-slate-800 text-slate-600",
};
const ESTADO_DOC_LABEL = { vigente: "Vigente", cobrado: "Cobrado", cancelado: "Cancelado", anulado: "Anulado", destruido: "Destruido" };
const ESTADO_DOC_ICON = { vigente: ShieldCheck, cobrado: ShieldCheck, cancelado: ShieldOff, anulado: ShieldAlert, destruido: ShieldOff };

// Buscador de cliente/proveedor para la carga manual — si no encuentra coincidencias
// permite igual escribir el nombre libremente (entidad no registrada en Catastro).
function EntidadSearchSelect({ token, logoTipo, tipo, value, onChange }) {
  const [query, setQuery] = useState(value?.razon_social || "");
  const [opciones, setOpciones] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [buscando, setBuscando] = useState(false);

  useEffect(() => { setQuery(value?.razon_social || ""); }, [value?.razon_social]);

  useEffect(() => {
    if (!abierto) return;
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const params = new URLSearchParams({ tipo });
        if (logoTipo) params.set("logo_tipo", logoTipo);
        if (query.trim()) params.set("search", query.trim());
        const res = await fetch(`${API}/admin/garantias/entidades-buscar?${params}`, { headers: { Authorization: `Bearer ${token}` } });
        setOpciones(res.ok ? await res.json() : []);
      } catch { setOpciones([]); }
      finally { setBuscando(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, abierto, tipo, logoTipo, token]);

  const elegir = (ent) => {
    onChange({ empresa_id: ent.id, razon_social: ent.nombre || ent.razon_social || "", ruc: ent.ruc || "" });
    setQuery(ent.nombre || ent.razon_social || "");
    setAbierto(false);
  };

  return (
    <div className="relative">
      <input
        value={query}
        onFocus={() => setAbierto(true)}
        onChange={e => {
          setQuery(e.target.value);
          setAbierto(true);
          // Si escribe libremente, se guarda el texto tal cual (sin empresa_id) — entidad no registrada.
          onChange({ empresa_id: null, razon_social: e.target.value, ruc: value?.ruc || "" });
        }}
        onBlur={() => setTimeout(() => setAbierto(false), 150)}
        placeholder={tipo === "recibida" ? "Buscar cliente o escribir nombre..." : "Buscar proveedor o escribir nombre..."}
        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-arandu-blue/50"
      />
      {abierto && (
        <div className="absolute z-10 mt-1 w-full bg-arandu-dark-light border border-white/10 rounded-lg shadow-xl max-h-48 overflow-y-auto">
          {buscando && <p className="px-3 py-2 text-xs text-slate-500">Buscando...</p>}
          {!buscando && opciones.length === 0 && (
            <p className="px-3 py-2 text-xs text-slate-500">Sin coincidencias — se usará el nombre escrito tal cual.</p>
          )}
          {!buscando && opciones.map(ent => (
            <button
              type="button"
              key={ent.id}
              onMouseDown={() => elegir(ent)}
              className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-white/10 transition-colors"
            >
              {ent.nombre || ent.razon_social}
              {ent.ruc && <span className="text-slate-500 text-xs ml-2">RUC/CI: {ent.ruc}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// embedded=true → se usa dentro de FinancieroPage (sin header propio ni min-h-screen)
export default function GarantiasPage({ embedded = false }) {
  const { token, hasPermission, activeEmpresaPropia } = useContext(AuthContext);

  const puedeVer    = hasPermission("garantias.ver");
  const puedeEmitir = hasPermission("garantias.emitir");
  const puedeCobrar = hasPermission("garantias.cobrar");
  const puedeAnular = hasPermission("garantias.anular");

  const logoTipo = activeEmpresaPropia?.slug || "";

  const [tipo, setTipo] = useState("recibidas"); // "recibidas" | "concedidas"
  const [garantias, setGarantias] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [estadoDocumento, setEstadoDocumento] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  // ── Multi-selección para el Protocolo de Entrega ────────────────────────
  const [seleccionados, setSeleccionados] = useState(new Set());
  const toggleSeleccionado = (id) => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSeleccionarTodos = () => {
    setSeleccionados(prev => {
      if (garantias.length > 0 && garantias.every(g => prev.has(g.id))) return new Set();
      return new Set(garantias.map(g => g.id));
    });
  };

  const [accion, setAccion] = useState(null); // { cuota, tipo: "anular"|"cancelar-documento"|"destruir" }
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  const [editando, setEditando] = useState(null); // cuota
  const [formEdit, setFormEdit] = useState({ numero_pagare: "", tipo_documento_firmante: "" });

  const [cobrando, setCobrando] = useState(null); // cuota
  const [cuentas, setCuentas] = useState([]);
  const [formCobro, setFormCobro] = useState({ cuenta_id: "", fecha_pago: hoyStr() });

  // ── Carga manual de garantía ────────────────────────────────────────────
  const formNuevaVacio = () => ({
    tipo_garantia: tipo, empresa_id: null, razon_social: "", ruc: "",
    doc_numero: "", monto: "", moneda: "PYG", fecha_vencimiento: "",
    numero_pagare: "", tipo_documento_firmante: "",
  });
  const [creando, setCreando] = useState(false);
  const [formNueva, setFormNueva] = useState(formNuevaVacio());

  const fetchGarantias = useCallback(async () => {
    if (!puedeVer) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (logoTipo) params.set("logo_tipo", logoTipo);
      params.set("tipo", tipo);
      if (estadoDocumento) params.set("estado_documento", estadoDocumento);
      if (search.trim()) params.set("search", search.trim());
      if (fechaDesde) params.set("fecha_desde", fechaDesde);
      if (fechaHasta) params.set("fecha_hasta", fechaHasta);
      const res = await fetch(`${API}/admin/garantias?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setGarantias(data.garantias || []);
        setKpis(data.kpis || null);
        setSeleccionados(new Set());
      }
    } catch { toast.error("Error al cargar garantías"); }
    finally { setLoading(false); }
  }, [puedeVer, logoTipo, tipo, estadoDocumento, search, fechaDesde, fechaHasta, token]);

  useEffect(() => { fetchGarantias(); }, [fetchGarantias]);

  const fetchCuentas = async () => {
    try {
      const params = logoTipo ? `?logo_tipo=${logoTipo}` : "";
      const res = await fetch(`${API}/admin/cuentas-bancarias${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCuentas(await res.json());
    } catch { /* silencioso */ }
  };

  // ── Acciones sobre el documento (anular / cancelar / destruir) ────────────
  const abrirAccion = (cuota, tipoAccion) => { setAccion({ cuota, tipo: tipoAccion }); setMotivo(""); };

  const confirmarAccion = async (e) => {
    e.preventDefault();
    if (!accion) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/garantias/${accion.cuota.id}/${accion.tipo}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ motivo }),
      });
      if (res.ok) {
        toast.success("Documento actualizado");
        setAccion(null); setMotivo("");
        fetchGarantias();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo actualizar el documento");
      }
    } catch { toast.error("Error de conexión"); }
    finally { setSaving(false); }
  };

  // ── Editar número de pagaré / tipo de documento ────────────────────────────
  const abrirEditar = (cuota) => {
    setEditando(cuota);
    setFormEdit({ numero_pagare: cuota.numero_pagare || "", tipo_documento_firmante: cuota.tipo_documento_firmante || "" });
  };

  const guardarEdicion = async (e) => {
    e.preventDefault();
    if (!editando) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/garantias/${editando.id}/documento`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(formEdit),
      });
      if (res.ok) {
        toast.success("Datos del pagaré actualizados");
        setEditando(null);
        fetchGarantias();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo guardar");
      }
    } catch { toast.error("Error de conexión"); }
    finally { setSaving(false); }
  };

  // ── Cobrar cuota (marca pagado + documento cobrado) ────────────────────────
  const abrirCobro = (cuota) => {
    setCobrando(cuota);
    setFormCobro({ cuenta_id: "", fecha_pago: hoyStr() });
    fetchCuentas();
  };

  const confirmarCobro = async (e) => {
    e.preventDefault();
    if (!cobrando) return;
    setSaving(true);
    try {
      const cuentaSel = cuentas.find(c => c.id === formCobro.cuenta_id);
      const res = await fetch(`${API}/admin/cuotas/cobrar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          cuota_ids: [cobrando.id],
          cuenta_id: formCobro.cuenta_id || "",
          cuenta_nombre: cuentaSel?.nombre || "",
          fecha_pago: formCobro.fecha_pago,
          moneda_pago: cobrando.moneda,
        }),
      });
      if (res.ok) {
        toast.success("Garantía cobrada");
        setCobrando(null);
        fetchGarantias();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo registrar el cobro");
      }
    } catch { toast.error("Error de conexión"); }
    finally { setSaving(false); }
  };

  // ── Carga manual de garantía (no ligada a ninguna factura/compra) ──────────
  const abrirNueva = () => { setFormNueva(formNuevaVacio()); setCreando(true); };

  const crearGarantiaManual = async (e) => {
    e.preventDefault();
    if (!formNueva.razon_social.trim()) {
      toast.error(formNueva.tipo_garantia === "recibida" ? "Indicá el cliente" : "Indicá el proveedor");
      return;
    }
    if (!formNueva.monto || Number(formNueva.monto) <= 0) {
      toast.error("El monto debe ser mayor a 0");
      return;
    }
    if (!formNueva.fecha_vencimiento) {
      toast.error("Indicá la fecha de vencimiento");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/garantias`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...formNueva, logo_tipo: logoTipo, monto: Number(formNueva.monto) }),
      });
      if (res.ok) {
        toast.success("Garantía registrada");
        setCreando(false);
        if (formNueva.tipo_garantia !== tipo) setTipo(formNueva.tipo_garantia);
        else fetchGarantias();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo registrar la garantía");
      }
    } catch { toast.error("Error de conexión"); }
    finally { setSaving(false); }
  };

  // ── Imprimir pagaré (comprobante simple) ───────────────────────────────────
  const imprimirPagare = (cuota) => {
    const w = window.open("", "_blank", "width=700,height=900");
    if (!w) return;
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    w.document.write(`
      <html><head><title>Pagaré ${esc(cuota.doc_numero)} - Cuota ${cuota.numero}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:40px;color:#111}
        h1{font-size:18px;border-bottom:2px solid #111;padding-bottom:10px}
        .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:20px}
        .field{border:1px solid #ccc;border-radius:6px;padding:8px 12px}
        .label{display:block;font-size:11px;color:#666;text-transform:uppercase}
        .value{font-size:14px;font-weight:600}
      </style></head><body>
        <h1>PAGARÉ — ${cuota.tipo_doc === "factura" ? "Garantía Recibida" : "Garantía Concedida"}</h1>
        <div class="grid">
          <div class="field"><span class="label">Documento origen</span><span class="value">${esc(cuota.doc_numero)}</span></div>
          <div class="field"><span class="label">Cuota</span><span class="value">N° ${esc(cuota.numero)}</span></div>
          <div class="field"><span class="label">${cuota.tipo_doc === "factura" ? "Cliente" : "Proveedor"}</span><span class="value">${esc(cuota.razon_social)}</span></div>
          <div class="field"><span class="label">Tipo documento firmante</span><span class="value">${esc(cuota.tipo_documento_firmante || "-")}</span></div>
          <div class="field"><span class="label">Monto</span><span class="value">${esc(fmtMoneda(cuota.monto, cuota.moneda))}</span></div>
          <div class="field"><span class="label">Vencimiento</span><span class="value">${esc(cuota.fecha_vencimiento)}</span></div>
          <div class="field"><span class="label">N° de pagaré</span><span class="value">${esc(cuota.numero_pagare || "-")}</span></div>
          <div class="field"><span class="label">Estado del documento</span><span class="value">${esc(ESTADO_DOC_LABEL[cuota.estado_documento] || cuota.estado_documento)}</span></div>
        </div>
      </body></html>
    `);
    w.document.close();
    w.print();
  };

  // ── Imprimir Protocolo de Entrega (acuse de recepción, multi-selección) ───
  // Junta los pagarés seleccionados y arma un acta para que el cliente/proveedor
  // firme como constancia de que recibió de vuelta el/los documento(s).
  // Si hay varias razones sociales distintas seleccionadas, arma un bloque de
  // firma independiente por cada una (cada quien firma solo lo suyo).
  const imprimirProtocoloEntrega = () => {
    const items = garantias.filter(g => seleccionados.has(g.id));
    if (items.length === 0) return;
    const w = window.open("", "_blank", "width=800,height=1000");
    if (!w) return;
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

    // Agrupar por razón social — cada grupo firma solo sus propios documentos
    const grupos = [];
    const grupoIdx = new Map();
    items.forEach(g => {
      const key = g.razon_social || "—";
      if (!grupoIdx.has(key)) { grupoIdx.set(key, grupos.length); grupos.push({ razon_social: key, tipo_doc: g.tipo_doc, items: [] }); }
      grupos[grupoIdx.get(key)].items.push(g);
    });

    const hoy = new Date().toLocaleDateString("es-PY");
    const empresaNombre = activeEmpresaPropia?.nombre || "";
    const empresaRuc = activeEmpresaPropia?.ruc || "";

    const bloqueGrupo = (grupo) => {
      const esCliente = grupo.tipo_doc === "factura";
      const filas = grupo.items.map(g => `
        <tr>
          <td>${esc(g.doc_numero || "—")}${g.creado_manualmente ? "" : ` — Cuota ${esc(g.numero)}`}</td>
          <td>${esc(g.numero_pagare || "-")}</td>
          <td style="text-align:right">${esc(fmtMoneda(g.monto, g.moneda))}</td>
          <td>${esc(g.fecha_vencimiento)}</td>
        </tr>`).join("");
      return `
        <div class="grupo">
          <p class="destinatario"><strong>${esCliente ? "Cliente" : "Proveedor"}:</strong> ${esc(grupo.razon_social)}</p>
          <table>
            <thead><tr><th>Documento origen</th><th>N° de pagaré</th><th>Monto</th><th>Vencimiento</th></tr></thead>
            <tbody>${filas}</tbody>
          </table>
          <p class="declaracion">
            Quien suscribe declara haber recibido en este acto, de parte de ${esc(empresaNombre || "la empresa")},
            el/los documento(s) (pagaré/s) detallado(s) arriba, en carácter de devolución${esCliente ? " por cancelación de la garantía" : ""},
            no teniendo nada más que reclamar por dicho concepto.
          </p>
          <div class="firma-grid">
            <div class="firma-box">
              <div class="linea"></div>
              <span class="firma-label">Firma</span>
            </div>
            <div class="firma-box">
              <div class="linea"></div>
              <span class="firma-label">Aclaración</span>
            </div>
            <div class="firma-box">
              <div class="linea"></div>
              <span class="firma-label">CI / RUC N°</span>
            </div>
            <div class="firma-box">
              <div class="linea"></div>
              <span class="firma-label">Fecha de recepción</span>
            </div>
          </div>
        </div>`;
    };

    w.document.write(`
      <html><head><title>Protocolo de Entrega de Garantías</title>
      <style>
        body{font-family:Arial,sans-serif;padding:36px;color:#111;font-size:13px}
        h1{font-size:18px;border-bottom:2px solid #111;padding-bottom:10px;margin-bottom:4px}
        .empresa{font-size:12px;color:#444;margin-bottom:4px}
        .fecha-emision{font-size:11px;color:#666;margin-bottom:24px}
        .grupo{margin-bottom:36px;page-break-inside:avoid}
        .destinatario{font-size:13px;margin-bottom:8px}
        table{width:100%;border-collapse:collapse;margin-bottom:10px}
        th,td{border:1px solid #ccc;padding:6px 8px;font-size:12px;text-align:left}
        th{background:#f2f2f2;text-transform:uppercase;font-size:10px;color:#555}
        .declaracion{font-size:11.5px;color:#333;line-height:1.5;margin:10px 0 20px}
        .firma-grid{display:grid;grid-template-columns:1fr 1fr;gap:24px 40px}
        .firma-box{padding-top:28px}
        .linea{border-bottom:1px solid #111;margin-bottom:4px}
        .firma-label{font-size:10px;color:#666;text-transform:uppercase}
      </style></head><body>
        <h1>PROTOCOLO DE ENTREGA DE GARANTÍAS</h1>
        ${empresaNombre ? `<p class="empresa">${esc(empresaNombre)}${empresaRuc ? ` — RUC ${esc(empresaRuc)}` : ""}</p>` : ""}
        <p class="fecha-emision">Constancia de devolución de pagaré(s) — emitido el ${hoy}</p>
        ${grupos.map(bloqueGrupo).join("")}
      </body></html>
    `);
    w.document.close();
    w.print();
  };

  if (!puedeVer) return null;

  const kGrupo = tipo === "recibidas" ? kpis?.recibidas : kpis?.concedidas;

  const content = (
    <div className={embedded ? "space-y-5" : "p-4 md:p-8 max-w-6xl mx-auto space-y-6"}>
      {/* Sub-tabs Recibidas / Concedidas */}
      <div className="flex gap-2">
        <button
          onClick={() => setTipo("recibidas")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tipo === "recibidas" ? "bg-arandu-blue text-white" : "bg-white/5 text-slate-400 hover:text-white"
          }`}
        >
          <ArrowDownCircle className="w-4 h-4" /> Recibidas (de clientes)
        </button>
        <button
          onClick={() => setTipo("concedidas")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tipo === "concedidas" ? "bg-arandu-blue text-white" : "bg-white/5 text-slate-400 hover:text-white"
          }`}
        >
          <ArrowUpCircle className="w-4 h-4" /> Concedidas (a proveedores)
        </button>
        <div className="ml-auto flex items-center gap-2">
          {seleccionados.size > 0 && (
            <button
              onClick={imprimirProtocoloEntrega}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-arandu-blue hover:bg-arandu-blue-dark text-white transition-colors"
              title="Imprimir acuse de recepción para que firmen la devolución de los pagarés seleccionados"
            >
              <ClipboardCheck className="w-4 h-4" /> Imprimir Protocolo de Entrega ({seleccionados.size})
            </button>
          )}
          {puedeEmitir && (
            <button
              onClick={abrirNueva}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
            >
              <Plus className="w-4 h-4" /> Nueva Garantía
            </button>
          )}
          <button onClick={fetchGarantias} className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors" title="Actualizar">
            <RefreshCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* KPIs */}
      {kGrupo && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-xs text-slate-500 mb-1">Total documentos</p>
            <p className="text-xl font-bold text-white">{kGrupo.count_total}</p>
          </div>
          <div className="bg-cyan-900/10 border border-cyan-500/20 rounded-xl p-4">
            <p className="text-xs text-cyan-400 mb-1">Vigentes (₲)</p>
            <p className="text-xl font-bold text-white">{fmtMoneda(kGrupo.monto_vigente_pyg, "PYG")}</p>
            {kGrupo.monto_vigente_usd > 0 && <p className="text-xs text-cyan-300 mt-0.5">{fmtMoneda(kGrupo.monto_vigente_usd, "USD")}</p>}
          </div>
          <div className="bg-red-900/10 border border-red-500/20 rounded-xl p-4">
            <p className="text-xs text-red-400 mb-1">Vencidas</p>
            <p className="text-xl font-bold text-white">{kGrupo.count_vencidas}</p>
            <p className="text-xs text-red-300 mt-0.5">{fmtMoneda(kGrupo.monto_vencidas_pyg, "PYG")}</p>
          </div>
          <div className="bg-emerald-900/10 border border-emerald-500/20 rounded-xl p-4">
            <p className="text-xs text-emerald-400 mb-1">Cobradas</p>
            <p className="text-xl font-bold text-white">{kGrupo.count_cobrado || 0}</p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por cliente/proveedor, N° doc o N° pagaré..."
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-arandu-blue/50"
          />
        </div>
        <select value={estadoDocumento} onChange={e => setEstadoDocumento(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none">
          <option value="">Todos los estados</option>
          {Object.keys(ESTADO_DOC_LABEL).map(k => <option key={k} value={k}>{ESTADO_DOC_LABEL[k]}</option>)}
        </select>
        <DateInput value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none" />
        <DateInput value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none" />
      </div>

      {/* Tabla */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-slate-400 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3 w-8">
                <input type="checkbox"
                  checked={garantias.length > 0 && garantias.every(g => seleccionados.has(g.id))}
                  onChange={toggleSeleccionarTodos}
                  className="accent-arandu-blue w-3.5 h-3.5"
                  title="Seleccionar todos (para el Protocolo de Entrega)"
                />
              </th>
              <th className="text-left px-4 py-3">{tipo === "recibidas" ? "Cliente" : "Proveedor"}</th>
              <th className="text-left px-4 py-3">Documento</th>
              <th className="text-left px-4 py-3">Vence</th>
              <th className="text-right px-4 py-3">Monto</th>
              <th className="text-left px-4 py-3">Pago</th>
              <th className="text-left px-4 py-3">Documento (pagaré)</th>
              <th className="text-right px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && (
              <tr><td colSpan={8} className="text-center py-8 text-slate-500">Cargando...</td></tr>
            )}
            {!loading && garantias.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-slate-500">Sin garantías para los filtros aplicados</td></tr>
            )}
            {!loading && garantias.map(g => {
              const Icon = ESTADO_DOC_ICON[g.estado_documento] || Shield;
              const puedeAccionar = g.estado_documento !== "destruido";
              return (
                <tr key={g.id} className="hover:bg-white/5">
                  <td className="px-4 py-3">
                    <input type="checkbox"
                      checked={seleccionados.has(g.id)}
                      onChange={() => toggleSeleccionado(g.id)}
                      className="accent-arandu-blue w-3.5 h-3.5"
                    />
                  </td>
                  <td className="px-4 py-3 text-white">{g.razon_social}</td>
                  <td className="px-4 py-3 text-slate-300">
                    {g.creado_manualmente ? (
                      <>
                        {g.doc_numero || "—"}
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-500/15 text-violet-300 border border-violet-500/25 align-middle">Manual</span>
                      </>
                    ) : (
                      <>{g.doc_numero} <span className="text-slate-500">— Cuota {g.numero}</span></>
                    )}
                    {g.numero_pagare && <div className="text-xs text-slate-500">Pagaré N° {g.numero_pagare}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-300">{g.fecha_vencimiento}</td>
                  <td className="px-4 py-3 text-right text-white font-medium">{fmtMoneda(g.monto, g.moneda)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${ESTADO_PAGO_BADGE[g.estado] || ""}`}>
                      {ESTADO_PAGO_LABEL[g.estado] || g.estado}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${ESTADO_DOC_BADGE[g.estado_documento] || ""}`}>
                      <Icon className="w-3 h-3" /> {ESTADO_DOC_LABEL[g.estado_documento] || g.estado_documento}
                    </span>
                    {g.tipo_documento_firmante && <div className="text-xs text-slate-500 mt-0.5">{g.tipo_documento_firmante}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => imprimirPagare(g)} title="Imprimir" className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded">
                        <Printer className="w-4 h-4" />
                      </button>
                      {puedeEmitir && puedeAccionar && (
                        <button onClick={() => abrirEditar(g)} title="Editar N° de pagaré" className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded">
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      {puedeCobrar && g.estado !== "pagado" && g.estado !== "cancelado" && (
                        <button onClick={() => abrirCobro(g)} title="Marcar cobrado/pagado" className="p-1.5 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30 rounded">
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      {puedeAnular && puedeAccionar && (
                        <>
                          <button onClick={() => abrirAccion(g, "anular")} title="Anular documento" className="p-1.5 text-amber-400 hover:text-amber-300 hover:bg-amber-900/30 rounded">
                            <Ban className="w-4 h-4" />
                          </button>
                          <button onClick={() => abrirAccion(g, "destruir")} title="Marcar destruido" className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal: acción (anular/cancelar/destruir) */}
      {accion && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
              <div>
                <h3 className="font-heading font-semibold">
                  {accion.tipo === "anular" ? "Anular pagaré" : accion.tipo === "destruir" ? "Marcar pagaré destruido" : "Cancelar documento"}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">{accion.cuota.doc_numero} — Cuota {accion.cuota.numero} — {accion.cuota.razon_social}</p>
              </div>
            </div>
            <form onSubmit={confirmarAccion} className="p-6 space-y-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Motivo (opcional)</label>
                <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/50 resize-none"
                  placeholder="Ej: documento extraviado, reemplazado por nuevo pagaré, etc." />
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={saving} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                  {saving ? "Guardando…" : "Confirmar"}
                </button>
                <button type="button" onClick={() => setAccion(null)} className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-2.5 rounded-lg text-sm transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: editar datos del pagaré */}
      {editando && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
              <FileSignature className="w-5 h-5 text-arandu-blue shrink-0" />
              <div>
                <h3 className="font-heading font-semibold">Datos del pagaré</h3>
                <p className="text-xs text-slate-500 mt-0.5">{editando.doc_numero} — Cuota {editando.numero}</p>
              </div>
            </div>
            <form onSubmit={guardarEdicion} className="p-6 space-y-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">N° de pagaré</label>
                <input value={formEdit.numero_pagare} onChange={e => setFormEdit(f => ({ ...f, numero_pagare: e.target.value }))}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-arandu-blue/50" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Tipo de documento del firmante</label>
                <select value={formEdit.tipo_documento_firmante} onChange={e => setFormEdit(f => ({ ...f, tipo_documento_firmante: e.target.value }))}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-arandu-blue/50">
                  <option value="">Sin especificar</option>
                  <option value="RUC">RUC</option>
                  <option value="CI">CI</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={saving} className="flex-1 bg-arandu-blue hover:bg-arandu-blue-dark text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                  {saving ? "Guardando…" : "Guardar"}
                </button>
                <button type="button" onClick={() => setEditando(null)} className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-2.5 rounded-lg text-sm transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: nueva garantía (carga manual) */}
      {creando && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
              <Plus className="w-5 h-5 text-emerald-400 shrink-0" />
              <div className="flex-1">
                <h3 className="font-heading font-semibold">Nueva garantía</h3>
                <p className="text-xs text-slate-500 mt-0.5">Carga manual — no queda ligada a ninguna factura o compra del sistema</p>
              </div>
              <button type="button" onClick={() => setCreando(false)} className="text-slate-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={crearGarantiaManual} className="p-6 space-y-4">
              <div className="flex gap-2">
                <button type="button"
                  onClick={() => setFormNueva(f => ({ ...f, tipo_garantia: "recibida", empresa_id: null, razon_social: "", ruc: "" }))}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formNueva.tipo_garantia === "recibida" ? "bg-arandu-blue text-white" : "bg-white/5 text-slate-400 hover:text-white"
                  }`}>
                  <ArrowDownCircle className="w-4 h-4" /> Recibida (cliente)
                </button>
                <button type="button"
                  onClick={() => setFormNueva(f => ({ ...f, tipo_garantia: "concedida", empresa_id: null, razon_social: "", ruc: "" }))}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formNueva.tipo_garantia === "concedida" ? "bg-arandu-blue text-white" : "bg-white/5 text-slate-400 hover:text-white"
                  }`}>
                  <ArrowUpCircle className="w-4 h-4" /> Concedida (proveedor)
                </button>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">
                  {formNueva.tipo_garantia === "recibida" ? "Cliente" : "Proveedor"}
                </label>
                <EntidadSearchSelect
                  token={token}
                  logoTipo={logoTipo}
                  tipo={formNueva.tipo_garantia}
                  value={formNueva}
                  onChange={(v) => setFormNueva(f => ({ ...f, ...v }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">RUC / CI</label>
                  <input value={formNueva.ruc} onChange={e => setFormNueva(f => ({ ...f, ruc: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-arandu-blue/50" />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Tipo de documento del firmante</label>
                  <select value={formNueva.tipo_documento_firmante} onChange={e => setFormNueva(f => ({ ...f, tipo_documento_firmante: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-arandu-blue/50">
                    <option value="">Sin especificar</option>
                    <option value="RUC">RUC</option>
                    <option value="CI">CI</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Referencia del documento (opcional)</label>
                <input value={formNueva.doc_numero} onChange={e => setFormNueva(f => ({ ...f, doc_numero: e.target.value }))}
                  placeholder="Ej: Contrato N° 001, Recibo, etc."
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-arandu-blue/50" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Monto</label>
                  <input type="number" min="0" step="0.01" value={formNueva.monto} onChange={e => setFormNueva(f => ({ ...f, monto: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-arandu-blue/50" required />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Moneda</label>
                  <select value={formNueva.moneda} onChange={e => setFormNueva(f => ({ ...f, moneda: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-arandu-blue/50">
                    <option value="PYG">₲ PYG</option>
                    <option value="USD">US$ USD</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Fecha de vencimiento</label>
                  <DateInput value={formNueva.fecha_vencimiento} onChange={e => setFormNueva(f => ({ ...f, fecha_vencimiento: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-arandu-blue/50" required />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">N° de pagaré (opcional)</label>
                  <input value={formNueva.numero_pagare} onChange={e => setFormNueva(f => ({ ...f, numero_pagare: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-arandu-blue/50" />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                  {saving ? "Guardando…" : "Registrar garantía"}
                </button>
                <button type="button" onClick={() => setCreando(false)} className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-2.5 rounded-lg text-sm transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: cobrar */}
      {cobrando && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
              <div>
                <h3 className="font-heading font-semibold">Cobrar / pagar garantía</h3>
                <p className="text-xs text-slate-500 mt-0.5">{cobrando.doc_numero} — Cuota {cobrando.numero} — {fmtMoneda(cobrando.monto, cobrando.moneda)}</p>
              </div>
            </div>
            <form onSubmit={confirmarCobro} className="p-6 space-y-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Cuenta</label>
                <select value={formCobro.cuenta_id} onChange={e => setFormCobro(f => ({ ...f, cuenta_id: e.target.value }))}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50">
                  <option value="">Sin cuenta (solo actualizar estado)</option>
                  {cuentas.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Fecha de pago</label>
                <DateInput value={formCobro.fecha_pago} onChange={e => setFormCobro(f => ({ ...f, fecha_pago: e.target.value }))}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50" required />
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                  {saving ? "Guardando…" : "Confirmar cobro"}
                </button>
                <button type="button" onClick={() => setCobrando(null)} className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-2.5 rounded-lg text-sm transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) return content;

  return (
    <div className="min-h-screen bg-arandu-dark">
      <header className="sticky top-0 z-30 bg-arandu-dark/80 backdrop-blur-lg border-b border-white/5">
        <div className="px-4 md:px-8 py-4 flex items-center gap-3">
          <a href="/sistema" className="text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </a>
          <div>
            <h1 className="font-heading text-xl font-bold text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-arandu-blue" /> Garantías
            </h1>
            <p className="text-slate-500 text-xs">Pagarés recibidos de clientes y concedidos a proveedores</p>
          </div>
        </div>
      </header>
      {content}
    </div>
  );
}
